import { DateOnlyInput } from '../components/DateOnlyInput';
import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router';
import { alphaApi as api } from './alpha-request';
import { ErrorState, Loading } from '../ui/State';
export function BankFeedAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building'),
    [data, setData] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [accounts, setAccounts] = useState<Record<string, any[]>>({}),
    [history, setHistory] = useState<any>(),
    [notice, setNotice] = useState(''),
    callback = useRef(false);
  const load = () => api(`/api/alpha/bank-feeds?building=${building}`).then(setData);
  async function run(url: string, b: object) {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api<any>(url, { method: 'POST', body: JSON.stringify(b) });
      if (building) await load();
      return r;
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    if (building) void load().catch(setError);
    const code = q.get('code'),
      state = q.get('state');
    if (code && state && !callback.current) {
      callback.current = true;
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (building ? '?building=' + building : ''),
      );
      void run('/api/alpha/bank-feeds/complete', { code, state }).then((r) => {
        if (r)
          setNotice(
            'Consent received. Return to the building bank-feed page to select the account.',
          );
      });
    }
  }, [building]);
  async function start(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = await run('/api/alpha/bank-feeds/connect', {
      bankAccountId: new FormData(e.currentTarget).get('account'),
      idempotencyKey: crypto.randomUUID(),
    });
    if (r?.url) window.location.assign(r.url);
  }
  return (
    <>
      <h1>Sandbox bank feed</h1>
      <p>
        This alpha imports test transactions only into a disposable database. Statement imports
        remain available for reconciliation; a feed does not prove opening or closing balances.
      </p>
      {Boolean(error) && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      {!building ? (
        <p>Open this page in the building whose test bank account you are connecting.</p>
      ) : !data && !error ? (
        <Loading />
      ) : (
        data && (
          <>
            <Link to={`/finance/reconciliation?building=${building}`}>Bank reconciliation</Link>
            {!data.configured ? (
              <p>Sandbox credentials and token encryption need configuration before connecting.</p>
            ) : (
              <form className="form" onSubmit={start}>
                <label>
                  Test bank account
                  <select name="account" required>
                    <option value="">Choose account</option>
                    {data.accounts.map((a: any) => (
                      <option key={a.id} value={a.id}>
                        {a.account_name}
                      </option>
                    ))}
                  </select>
                </label>
                <button disabled={busy}>Connect or renew sandbox consent</button>
              </form>
            )}
            {data.connections.map((c: any) => (
              <section className="panel" key={c.id}>
                <h2>{c.account_name}</h2>
                <p>
                  {c.status} · Imported through {c.cursor_date ?? 'not yet imported'}
                </p>
                <p>{c.last_error}</p>
                {c.status === 'Choose account' && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          const r = await api<{ accounts: any[] }>(
                            `/api/alpha/bank-feeds/${c.id}/accounts`,
                          );
                          setAccounts({ ...accounts, [c.id]: r.accounts });
                        } catch (e) {
                          setError(e);
                        }
                      }}
                    >
                      Load authorised accounts
                    </button>
                    {accounts[c.id] && (
                      <form
                        className="form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const f = new FormData(e.currentTarget);
                          void run(`/api/alpha/bank-feeds/${c.id}/select`, {
                            providerAccountId: f.get('account'),
                            from: f.get('from'),
                            idempotencyKey: crypto.randomUUID(),
                          });
                        }}
                      >
                        <label>
                          Authorised sandbox account
                          <select name="account" required>
                            {accounts[c.id]!.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.name} · {a.currency}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Import from
                          <DateOnlyInput name="from" required />
                        </label>
                        <button disabled={busy}>Use this account</button>
                      </form>
                    )}
                  </>
                )}
                {['Connected', 'Paused'].includes(c.status) && (
                  <form
                    className="form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const r = await run(`/api/alpha/bank-feeds/${c.id}/sync`, {
                        to: new FormData(e.currentTarget).get('to'),
                        idempotencyKey: crypto.randomUUID(),
                      });
                      if (r)
                        setNotice(
                          `Import saved. ${r.imported ?? 0} new, ${r.duplicates ?? 0} duplicates, ${r.review ?? 0} requiring review.`,
                        );
                    }}
                  >
                    <label>
                      Import through
                      <DateOnlyInput name="to" required />
                    </label>
                    <button disabled={busy}>Import transactions</button>
                  </form>
                )}
                <button
                  onClick={async () => {
                    try {
                      setHistory(await api(`/api/alpha/bank-feeds/${c.id}/history`));
                    } catch (e) {
                      setError(e);
                    }
                  }}
                >
                  View import history
                </button>
                <button
                  disabled={busy || c.status === 'Disconnected'}
                  onClick={() =>
                    void run(`/api/alpha/bank-feeds/${c.id}/disconnect`, {
                      idempotencyKey: crypto.randomUUID(),
                    })
                  }
                >
                  Disconnect
                </button>
              </section>
            ))}
          </>
        )
      )}
      {history && (
        <section className="panel">
          <h2>Import history</h2>
          {history.imports.map((i: any) => (
            <p key={i.id}>
              {i.from_date} to {i.to_date}
            </p>
          ))}
          <h3>Transactions requiring review</h3>
          {history.exceptions.map((e: any) => (
            <p key={e.id}>{e.reason}</p>
          ))}
        </section>
      )}
    </>
  );
}
